/**
 * mentionAddConsent — mentioning someone who is NOT in the thread asks before
 * it adds them (Miles, 2026-09-15).
 *
 * The finding, from the owner's own thread: "@stephen and @joshuav going back
 * to this thread…" was followed one second later by "Miles Maggio added Joshua
 * Villalta". He had meant @justin. The on-send summon added every mentioned
 * teammate who was not yet in the thread, silently, under the sender's own
 * authority — a membership change as a side effect of typing a name.
 *
 * The rule now: adding a person to a thread is an EXPLICIT act, never a side
 * effect. The @ list marks who is not in the thread; picking such a person,
 * for a viewer the manage predicate admits (creator ‖ owner ‖ admin ‖
 * `threads.manage` — the same rule the add-people panel and the server use),
 * ASKS first and records the consent; the send adds only the people whose
 * consent was recorded. Anyone else mentioned from outside the thread is named
 * in a line under the message instead of being added. A viewer who may not add
 * people is told who can, in the same words the add-people panel uses.
 *
 * Pure: the decision, the partition and the copy (D9 — code authors chrome
 * and refusal copy only), so the rule is unit-tested without React.
 */
import type { MentionCandidate } from './mentionAutocomplete';

export type MentionSelectionDecision = 'insert' | 'ask-add' | 'cannot-add';

/**
 * What picking `candidate` from the @ list does. `inThread` ABSENT means the
 * caller has no participant list to judge by (an unknown thread) and the pick
 * inserts, exactly as before; `false` asks or refuses by the viewer's authority.
 */
export function decideMentionSelection(
  candidate: Pick<MentionCandidate, 'uid' | 'inThread'>,
  ctx: { canAddPeople: boolean },
): MentionSelectionDecision {
  if (candidate.uid === 'assistant') return 'insert';
  if (candidate.inThread !== false) return 'insert';
  return ctx.canAddPeople ? 'ask-add' : 'cannot-add';
}

/**
 * Of the mentioned people who are NOT in the thread, who is added (consent
 * recorded) and who is held back (named under the message, never added).
 * People already in the thread are neither — a mention of a participant is a
 * mention, and needs nothing.
 */
export function partitionMentionedOutsiders(
  mentioned: ReadonlyArray<string>,
  existing: ReadonlySet<string>,
  confirmed: ReadonlySet<string>,
): { add: string[]; heldBack: string[] } {
  const add: string[] = [];
  const heldBack: string[] = [];
  const seen = new Set<string>();
  for (const raw of mentioned) {
    const uid = (raw ?? '').trim();
    if (!uid || seen.has(uid) || existing.has(uid)) continue;
    seen.add(uid);
    (confirmed.has(uid) ? add : heldBack).push(uid);
  }
  return { add, heldBack };
}

const joinNames = (names: ReadonlyArray<string>, es: boolean): string => {
  const list = names.filter((n) => n && n.trim());
  if (list.length <= 1) return list[0] ?? '';
  const last = list[list.length - 1];
  return `${list.slice(0, -1).join(', ')} ${es ? 'y' : 'and'} ${last}`;
};

/** Chrome + refusal copy for the ask (D9). English by default, Spanish on request. */
// copy: mention_add_consent
export const MENTION_ADD_COPY = {
  hint: (es = false) => (es ? 'Menciona a un compañero' : 'Mention a teammate'),
  notInThread: (es = false) => (es ? 'No está en este hilo' : 'Not in this thread'),
  inThread: (es = false) => (es ? 'En este hilo' : 'In this thread'),
  askAdd: (name: string, es = false) =>
    es ? `${name} no está en este hilo. ¿Añadirle?` : `${name} isn't in this thread. Add them?`,
  askAddHint: (es = false) =>
    es
      ? 'Verá este hilo desde ahora y recibirá tu mención.'
      : "They'll see this thread from here on and get your mention.",
  confirm: (es = false) => (es ? 'Añadir y mencionar' : 'Add and mention'),
  cancel: (es = false) => (es ? 'Cancelar' : 'Cancel'),
  /** The viewer may not add people — the add-people panel's own words. */
  cannotAdd: (name: string, es = false) =>
    es
      ? `${name} no está en este hilo. Quien empezó el hilo, o un propietario o administrador del espacio, puede añadirle.`
      : `${name} isn't in this thread. The person who started it, or a workspace owner or admin, can add them.`,
  /**
   * Under a SENT message: people mentioned from outside the thread who were
   * not added, because nothing asked. Two shapes — the viewer may add people
   * (say how), or may not (say who can).
   */
  heldBackCanAdd: (names: ReadonlyArray<string>, es = false) => {
    const who = joinNames(names, es);
    const plural = names.length > 1;
    return es
      ? `${who} no ${plural ? 'están' : 'está'} en este hilo, así que no verá${plural ? 'n' : ''} este mensaje. Para añadir${plural ? 'les' : 'le'}, elige su nombre en la lista de @ o usa "add people".`
      : `${who} ${plural ? "aren't" : "isn't"} in this thread, so ${plural ? 'they' : 'they'} won't see this message. To add ${plural ? 'them' : 'them'}, pick their name from the @ list or use "add people".`;
  },
  heldBackCannotAdd: (names: ReadonlyArray<string>, es = false) => {
    const who = joinNames(names, es);
    const plural = names.length > 1;
    return es
      ? `${who} no ${plural ? 'están' : 'está'} en este hilo. Quien empezó el hilo, o un propietario o administrador del espacio, puede añadir${plural ? 'les' : 'le'}.`
      : `${who} ${plural ? "aren't" : "isn't"} in this thread. The person who started it, or a workspace owner or admin, can add them.`;
  },
} as const;
